import styled, { css } from 'styled-components';
import { maxWidth, minWidth, noTouch } from '@/assets/styles/mixins.css';
import vars from '@/assets/styles/vars';
import Container from './StyledContainer';
import Section from './StyledSection';
import Anchors from './StyledAnchors';
import Select from './StyledSelect';
import ArrowButton from './StyledArrowButton';
import Button from './StyledButton';
import Title from './StyledTitle';
import Hero from './StyledHero';
import PartitionCard from './StyledPartitionCard';

// Don't apply "overflow: hidden;" to Content block: it will break side sticky element and make dropdown filters invisible on mobile when there is not enough content
const Content = styled(Section).attrs({
  pb: 'none',
})`
  ${(props) =>
    props.pageType === 'direction' &&
    css`
      ${Content.ColumnSide},
      .button-wrap {
        ${maxWidth(vars.breakpoints.tablet - 1)`
          display: none;
        `}
      }
    `}

  .no-content-swiping {
    overflow: hidden;
  }
`;

Content.Container = styled(Container)`
${(props) =>
    props.hiddenMobile &&
    css`
    ${maxWidth(vars.breakpoints.tablet)`
      display: none;
    `}
  `}
  ${(props) =>
    props.hiddenDesktop &&
    css`
      display: none;
    `}
${(props) =>
    props.visibleMobile &&
    css`
    display: none;
    ${maxWidth(vars.breakpoints.tablet)`
      display: block;
    `}
  `}
`;

Content.Columns = styled.div`
  display: grid;
  grid-template-columns: 290px minmax(0, 1fr);
  grid-gap: 32px 40px;

  ${maxWidth(vars.breakpoints.tablet)`
    grid-template-columns: minmax(0, 1fr);
    grid-gap: 30px;

    ${Anchors},
    ${Anchors.Link} {
      display: none;
    }
  `}

  ${(props) =>
    props.isAnchorsVisible &&
    css`
      ${maxWidth(vars.breakpoints.tablet + 1)`
        ${Anchors} {
          display: block;
        }
        ${Anchors.Link} {
          display:flex;
        }
        ${Content.Button} {
          display: flex;
        }
        ${Content.Side} {
        padding-bottom:0;
        }
      `}

      ${Content.SliderContent} > .swiper-container > .swiper-wrapper > .swiper-slide {
        padding-bottom: 110px !important;
        ${maxWidth(vars.breakpoints.tablet)`
          padding-bottom: 60px !important;
        `}
      }
    `}

    ${(props) =>
    !props.isAnchorsVisible &&
    css`
      ${Content.SliderContent} > .swiper-container > .swiper-wrapper {
        ${maxWidth(vars.breakpoints.tablet)`
            flex-direction:column;
            transform:none !important;
            height:auto !important;
        `}
        & >   .swiper-slide {
          ${maxWidth(vars.breakpoints.tablet)`
            margin:0 !important;
          `}
        }
       
      }
      .button-wrap {
        ${maxWidth(vars.breakpoints.tablet)`
          display:none;
        `}
      }
    `}



  ${(props) =>
    props.half &&
    css`
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      gap: 0;

      ${maxWidth(vars.breakpoints.tablet)`
        gap: 60px;
        padding:60px 0  0;
      `}

      ${Content.Side},
      ${Content.Loadable} {
        padding: 70px 60px;
        max-width: calc(${vars.container.maxWidth}px / 2 + ${vars.container.spaceDesktop}px);
        width: 100%;

        ${maxWidth(vars.breakpoints.tablet)`
          padding:0;
          max-width: unset;
        `}
      }
      ${Content.Side},
      ${Content.ColumnMain} {
        height: max(calc(100vh - 209px), 100%);
        overflow-y: auto;
        overflow-x: hidden;
        &::-webkit-scrollbar {
          width: 4px;
          background: #f0f0f0;
        }
        &::-webkit-scrollbar-thumb {
          width: 4px;
          background: #e2e2e2;
          border-radius: 2px;
        }

        ${maxWidth(vars.breakpoints.tablet)`
          height: calc(100vh - 156px);
          max-height: unset;
        `}
      }

      ${Content.Side} {
        top: 35px;
        max-height: fit-content;
        padding-bottom: 30px;
        &::-webkit-scrollbar {
          width: 0;
        }
        &::-webkit-scrollbar-thumb {
          width: 0;
        }
        ${maxWidth(vars.breakpoints.tablet)`
          height:unset;
          top:0;
          padding-bottom:60px;
        `}
      }
    `}

    ${(props) =>
    props.reverse &&
    css`
      ${Content.Loadable} {
        position: absolute;
        ${maxWidth(vars.breakpoints.tablet)`
          position: relative;
        `}
      }
      ${Content.ColumnMain} {
        order: -1;
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        position: relative;
        ${maxWidth(vars.breakpoints.tablet)`
          align-items: unset;
          width:calc(100% + 80px);
          margin-left:-40px;
          padding: 0 40px 60px;
          ${Title} {
            display:none;
          }
        `}
        ${maxWidth(vars.breakpoints.mobile)`
          align-items: unset;
          width:calc(100% + 40px);
          margin-left:-20px;
          padding: 0 20px 60px;
        `}
      }
      ${Content.Img} {
        img {
          background: #f8f8f8;
        }
      }
    `}

  ${(props) =>
    props.firstBlock &&
    css`
      ${maxWidth(vars.breakpoints.tablet)`
        padding:0;
      `}
      ${Content.Loadable} {
        position: static;
      }
      ${Content.Side},
      ${Content.ColumnMain} {
        height: max(calc(100vh - 209px), 100%);
      ${Content.ColumnMain} {
        ${maxWidth(vars.breakpoints.tablet)`
          height: unset;
        `}
      }
    `}
`;

Content.ColumnSide = styled.div``;

Content.ColumnMain = styled.div`
  scroll-behavior: smooth;
  ${Section}:first-child {
    padding-top: 0;
  }
  ${(props) =>
    props.bg &&
    css`
      background: #f8f8f8;
    `}
`;

Content.Side = styled.aside`
  position: sticky;
  top: 130px;
  z-index: 2;
  width: 100%;
  padding-bottom: 120px;

  ${Select} {
    margin-bottom: 20px;
  }

  ${maxWidth(vars.breakpoints.tablet)`
    padding-bottom: 100px;
  `}

  ${maxWidth(vars.breakpoints.tablet - 1)`
    position: relative;
    top: 0;
    padding-bottom: 0;

    ${Select} {
      margin-bottom: 0;
    }
  `}

  ${(props) =>
    props.hiddenTablet &&
    css`
      ${maxWidth(vars.breakpoints.tablet)`
          display:none;
        `}
    `}
`;
Content.Select = styled.div`
  display: none;
  ${maxWidth(vars.breakpoints.tablet)`
    display:block;
  `}
`;

Content.SliderContent = styled.div`
  & > .swiper-container {
    width: calc(100% + 40px);
    margin-left: -20px;
    padding: 0px 20px;
  }
  & > .swiper-container:not(.swiper-container-initialized) > .swiper-wrapper > .swiper-slide {
    margin-right: 50px;
  }
`;
Content.BeforeAfter = styled.div`
  margin-top: 20px;
  width: calc(100% + 40px);
  padding: 0 20px;
  margin-left: -20px;
  overflow: hidden;

  .swiper-container {
    width: 100%;
    overflow: visible;
  }

  .swiper-wrapper {
    align-items: stretch;
  }

  .swiper-slide {
    height: unset;
  }

  .swiper-slide picture {
    height: auto;
  }

  .swiper-slide img {
    position: static;
    height: 100%;
    width: 100%;
    object-fit: cover;
  }

  .swiper-button-next,
  .swiper-button-prev {
    width: 47px;
    height: 47px;
    top: 50%;
    background-repeat: no-repeat;
    background-position: center;
  }

  .swiper-button-next {
    right: 0;
    transform: translate(50%, -50%);
    background-image: url("data:image/svg+xml,%3Csvg width='9' height='17' viewBox='0 0 7 13' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0.470001 1.00998L6.28571 6.82568L0.470001 12.6414' stroke='%23B69B64'/%3E%3C/svg%3E%0A");
  }

  .swiper-button-prev {
    left: 0;
    transform: translate(-50%, -50%);
    background-image: url("data:image/svg+xml,%3Csvg width='9' height='17' viewBox='0 0 8 13' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M7.18137 12.6414L1.36566 6.82568L7.18137 1.00998' stroke='%23B69B64'/%3E%3C/svg%3E%0A");
  }
`;
Content.BeforeAfterSlide = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
Content.BeforeAfterItems = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(270px, 1fr));
  gap: 30px;
`;
Content.BeforeAfterItem = styled.div`
  display: flex;
  gap: 5px;
`;

Content.BeforeAfterPart = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 50%;

  & > div:first-child {
    height: 100%;
  }
  picture img {
    aspect-ratio: 140/201;

    ${maxWidth(vars.breakpoints.tablet)`
      aspect-ratio: 157/186;
    `}
  }
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

Content.Links = styled.div`
  border-top: 1px solid var(--color-decor);
  padding-top: 30px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  ${Select.RadioTitle},
  ${Select.Item}:first-child ${Select.RadioTitle},
  ${Select.Item}:last-child ${Select.RadioTitle} {
    padding: 0;
    font-size: 18px;
    line-height: 27px;
    display: flex;
    align-items: center;
  }
  ${maxWidth(vars.breakpoints.tablet)`
    display:none;
  `}
`;

Content.BreadcrumbsContainer = styled.div`
  margin-top: 20px;
  margin-bottom: -20px;

  ${maxWidth(vars.breakpoints.tablet)`
    margin-bottom:0;
  `}

  ${(props) =>
    props.text &&
    css`
      margin-top: 30px;
      margin-bottom: 30px;

      ${maxWidth(vars.breakpoints.tablet)`
        margin-top: 20px;
        margin-bottom:40px;
      `}
    `}

   ${(props) =>
    props.spec &&
    css`
      margin-top: 0;
      margin-bottom: 30px;
      padding-bottom: 15px;
      border-bottom: 1px solid #e2e2e2;

      ${maxWidth(vars.breakpoints.tablet)`
        margin-top: 0;
        margin-bottom: 30px;
      `}
    `}
`;

Content.ParentLink = styled.a`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  margin-top: -12px;
  margin-bottom: -12px;
  padding: 12px 0;
  font-weight: bold;
  font-size: 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #323232;

  svg {
    max-height: none;
    margin-right: 8px;
    transition: transform 0.3s ease-in-out;
  }

  &:hover {
    svg {
      transform: translateX(-6px);
    }
  }

  
  ${maxWidth(vars.breakpoints.tablet)`
    margin-top: 12px;
  `}
`;

Content.DirectoryLink = styled.a`
  font-weight: 300;
  font-size: 32px;
  letter-spacing: -0.01em;
  white-space: pre-wrap;
  color: #323232;

  svg {
    width: 26px;
    height: auto;
    margin-left: 20px;
    vertical-align: middle;
    max-height: none;
    transform: rotate(180deg);
    transition: transform 0.3s ease-in-out;
  }

  &:hover {
    svg {
      transform: translateX(6px) rotate(180deg);
    }
  }

  ${maxWidth(vars.breakpoints.tablet)`
    font-size: 26px;

    svg {
      width: 22px;
      margin-left: 18px;
    }
  `}

  ${maxWidth(vars.breakpoints.mobile)`
    svg {
      display: none;
    }
  `}
`;

Content.Button = styled.div`
  --button-width: 260px;
  margin-top: 20px;

  ${maxWidth(vars.breakpoints.tablet - 1)`
    display: none;
  `}
`;

Content.VideoLink = styled.div`
  --vlink-icon-width: 64px;
  margin-top: 20px;

  ${maxWidth(vars.breakpoints.sDesktop)`
    --vlink-icon-width: 56px;
    --vlink-icon-space: 20px;
  `}

  ${maxWidth(vars.breakpoints.tablet)`
    --vlink-icon-width: 46px;
    --vlink-font-size: 14px;
    --vlink-icon-space: 16px;
    padding-bottom: 20px;
  `}
`;

Content.Specialists = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  column-gap: 30px;
  row-gap: 50px;

  ${maxWidth(vars.breakpoints.xsDesktop)`
    grid-template-columns: repeat(2, minmax(0, 1fr));
  `}

  ${maxWidth(vars.breakpoints.lMobile)`
    grid-template-columns: minmax(0, 1fr);
  `}

  ${(props) =>
    props.items &&
    props.items == 4 &&
    css`
      grid-template-columns: repeat(4, minmax(0, 1fr));
      row-gap: 60px;
      ${maxWidth(vars.breakpoints.xsDesktop)`
        grid-template-columns: repeat(3, minmax(0, 1fr));
      `}
      ${maxWidth(900)`
        grid-template-columns: repeat(2, minmax(0, 1fr));
        row-gap: 30px;
      `}
      ${maxWidth(vars.breakpoints.lMobile)`
        grid-template-columns: minmax(0, 1fr);
      `}
    `}
`;

Content.Cards = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);

  ${PartitionCard.Content} {
    padding: 38px 32px;
  }
  ${PartitionCard.Title} {
    text-transform: unset;
    font-size: 22px;
  }

  ${maxWidth(vars.breakpoints.lMobile)`
    grid-template-columns: minmax(0, 1fr);
  `}

  ${(props) =>
    props.slider &&
    css`
      display: flex;

      .swiper-container {
        width: 100%;
        overflow: visible;
      }

      .swiper-button-next,
      .swiper-button-prev {
        width: 32px;
        height: 32px;
        top: 50%;
        background-repeat: no-repeat;
        background-position: center;
      }

      .swiper-button-next {
        right: 0;
        transform: translate(50%, -50%);
        background-image: url("data:image/svg+xml,%3Csvg width='7' height='13' viewBox='0 0 7 13' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0.470001 1.00998L6.28571 6.82568L0.470001 12.6414' stroke='%23B69B64'/%3E%3C/svg%3E%0A");
      }

      .swiper-button-prev {
        left: 0;
        transform: translate(-50%, -50%);
        background-image: url("data:image/svg+xml,%3Csvg width='8' height='13' viewBox='0 0 8 13' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M7.18137 12.6414L1.36566 6.82568L7.18137 1.00998' stroke='%23B69B64'/%3E%3C/svg%3E%0A");
      }
    `}

  ${(props) =>
    props.items &&
    css`
      gap: 20px;
      ${maxWidth(vars.breakpoints.lMobile)`
          grid-template-columns: minmax(0, 1fr);
        `}

      ${PartitionCard} {
        overflow: hidden;
        &:hover {
          ${PartitionCard.Title} {
            ${minWidth(vars.breakpoints.tablet + 1)`
              margin: 0 0 auto;
              top: 33px;
              bottom: unset;
            `}
          }
        }
      }

      ${PartitionCard.Content} {
        padding: 31px 38px;
      }

      ${PartitionCard.Bg}::after {
        min-height: 310px;
      }

      ${PartitionCard.Title} {
        position: absolute;
        bottom: 31px;
        left: 38px;
        width: calc(100% - (38px * 2));
        transition: 0.55s ease-in 0.15s;
        ${maxWidth(vars.breakpoints.tablet)`
              padding-right: 27px;
            `}
      }

      ${PartitionCard.Arrow} {
        top: unset;
        bottom: 31px;
        right: 36px;
        background-color: unset;
        background-image: url("data:image/svg+xml,%3Csvg width='17' height='15' viewBox='0 0 17 15' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cline x1='4.37114e-08' y1='7.5' x2='16' y2='7.5' stroke='white'/%3E%3Cpath d='M9.25974 0.74026L16.1494 7.62988L9.25974 14.5195' stroke='white'/%3E%3C/svg%3E%0A");
        background-position: center;
        background-repeat: no-repeat;
        border: 1px solid #fff;
        transform: unset;
        svg {
          display: none;
        }
        ${maxWidth(vars.breakpoints.tablet)`
              border:none;
            `}
      }
    `}

  ${(props) =>
    props.items === 3 &&
    css`
      grid-template-columns: repeat(3, minmax(0, 1fr));
      ${maxWidth(vars.breakpoints.xsDesktop)`
        grid-template-columns: repeat(2, minmax(0, 1fr));
      `}

      ${maxWidth(vars.breakpoints.mTablet)`
        grid-template-columns: minmax(0, 1fr);
      `}
    `}
  ${(props) =>
    props.items === 4 &&
    css`
      grid-template-columns: repeat(4, minmax(0, 1fr));
      ${maxWidth(vars.breakpoints.xsDesktop)`
          grid-template-columns: repeat(3, minmax(0, 1fr));
      `}
      ${maxWidth(900)`
          grid-template-columns: repeat(2, minmax(0, 1fr));
      `}
    `}

  ${(props) =>
    props.type === 'elements' &&
    css`
      gap: 30px;

      ${maxWidth(vars.breakpoints.xsDesktop)`
        grid-template-columns: minmax(0, 1fr);
    `}

      ${maxWidth(vars.breakpoints.tablet)`
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        gap: 20px;
    `}

      ${maxWidth(900)`
        grid-template-columns: minmax(0, 1fr);
      `}

    ${PartitionCard} {
        &:hover {
          ${PartitionCard.Title} {
            margin: 0 0 10px;
          }
        }
      }

      ${PartitionCard.Content} {
        padding: 38px 40px;
        position: relative;
        z-index: 1;
        min-height: 433px;
        ${maxWidth(vars.breakpoints.tablet)`
        padding: 35px 28px;
        min-height: unset;
    `}
      }

      ${PartitionCard.Bg} {
        position: absolute;
        top: 0;
        left: 0;
        min-height: 433px;
      }

      ${PartitionCard.Bg}::after {
        min-height: 433px;
        position: relative;
        z-index: 1;
      }

      ${PartitionCard.Title} {
        position: static;
        font-size: 28px;
        margin: 0 0 10px;
        width: 100%;
        transform: none;
        --card-title-color: #fff;

        ${minWidth(vars.breakpoints.tablet + 1)`
        transform: none;
      `}
        ${maxWidth(vars.breakpoints.tablet)`
        padding-right: 0;
      `}
      }

      ${Content.Html} {
        color: var(--color-light);
      }
    `}

  ${(props) =>
    props.type === 'images' &&
    css`
      gap: 30px;
      picture {
        display: block;
        height: 343px;
      }
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      ${maxWidth(vars.breakpoints.xsDesktop)`
        grid-template-columns: minmax(0, 1fr);
      `}

      ${maxWidth(vars.breakpoints.tablet)`
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        gap: 20px;
      `}

      ${maxWidth(900)`
        grid-template-columns: minmax(0, 1fr);
      `}
      ${maxWidth(vars.breakpoints.lMobile)`
          picture {
            height:234px;
          }
      `}
    `}

  ${(props) =>
    props.type === 'directions' &&
    css`
      ${PartitionCard.Title} {
        text-transform: uppercase;
      }
      ${PartitionCard.Arrow} {
        width: 20px;
        height: 30px;
        top:16px;
        right: 20px;
      }
      ${maxWidth(vars.breakpoints.xsDesktop)`
        ${PartitionCard.Bg}::after {
          min-height: 342px;
        }
      `}

      ${maxWidth(vars.breakpoints.tablet)`
        ${PartitionCard.Bg}::after {
          min-height: 342px;
        }
      `}
      ${maxWidth(800)`
        grid-template-columns: 1fr;
      `}

      ${maxWidth(vars.breakpoints.mobile)`
        ${PartitionCard.Bg}::after {
          min-height: 200px;
        }
      `}
    `}

  ${(props) =>
    props.type === 'services' &&
    css`
      ${PartitionCard.Title} {
        display:block;
      }
    `}
`;

Content.Html = styled.div`
  font-weight: normal;
  font-size: 16px;
  line-height: 1.6;
  color: var(--color-secondary);

  ${({ size }) => {
    switch (size) {
      case 'large':
        return css`
          font-size: 20px;
          ${maxWidth(vars.breakpoints.tablet)`
            font-size: 18px;
          `}
        `;
      case 'medium':
        return css`
          font-size: 18px;
          b,
          strong {
            font-size: 20px;
          }
        `;
      case 'small':
        return css`
          font-size: 14px;
        `;
      default:
        return css`
          font-size: 16px;
          ${maxWidth(vars.breakpoints.tablet)`
            font-size: 16px;
          `}
        `;
    }
  }}

  ${(props) =>
    props.black &&
    css`
      color: var(--color-main);
    `}

    ${(props) =>
    props.dark &&
    css`
      color: #000;
    `}

    ${(props) =>
    props.center &&
    css`
      text-align: center;
    `}

    ${(props) =>
    props.tabletLeft &&
    css`
      ${maxWidth(vars.breakpoints.tablet)`
      text-align: left;
      `}
    `}
    

  b {
    font-weight: bold;
  }

  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-top: 40px;
    margin-bottom: 20px;
    font-size: 28px;
    font-weight: 400;
    line-height: 1.16;
    letter-spacing: -0.01em;
    color: var(--color-main);

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  h3 {
    font-size: 24px;
    color: var(--color-decor);
    &.black {
      color: var(--color-main);
    }
  }

  h4 {
    font-size: 24px;
  }

  h5 {
    font-size: 22px;
  }

  h6 {
    font-size: 20px;
    color: var(--color-decor);
  }

  p {
    width: 100%;
    margin-bottom: 15px;
    white-space: pre-wrap;

    &:last-child {
      margin-bottom: 0;
    }
  }

  a {
    color: var(--color-decor);
    text-decoration-line: underline;
    transition: color 0.3s ease;


    ${noTouch()`
      &:hover {
        color: var(--color-decor-hover);
      }
    `}
  }

  img {
    width: auto;
    max-width: 100%;
    height: auto;
    max-height: 400px;
    margin: 40px 0;

    ${maxWidth(vars.breakpoints.mobile)`
      margin: 32px 0;
    `}
  }

  ul,
  ol {
    margin-top: 24px;
    margin-bottom: 24px;
    padding-left: 22px;

    li {
      position: relative;
      margin-bottom: 16px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul > li::before {
    content: '';
    position: absolute;
    top: 0.7em;
    right: calc(100% + 16px);
    display: block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--color-decor);
  }

  ol {
    counter-reset: ol-counter;
    padding-left: 0;
    li {
      counter-increment: ol-counter;

      &::before {
        content: counter(ol-counter) '.';
        display: inline-block;
        min-width: 26px;
        padding-right: 12px;
        font-size: inherit;
        font-weight: 500;
        color: currentColor;
      }
    }
  }
`;

Content.Img = styled.div`
  position: relative;
  width: 100%;

  &:not(:first-child) {
    margin-top: 40px;

    ${maxWidth(vars.breakpoints.mobile)`
      margin-top: 32px;
    `}
  }

  &:not(:last-child) {
    margin-bottom: 40px;

    ${maxWidth(vars.breakpoints.mobile)`
      margin-bottom: 32px;
    `}
  }

  &::after {
    content: '';
    display: block;
  }

  img {
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  ${(props) =>
    props.fullsize &&
    css`
      width: calc(100% + 120px);
      margin-left: -60px;
      margin-top: -70px;
      height: 100%;
      min-height: calc(100vh - 311px);
      position: static;

      img {
        position: absolute;
      }
      ${maxWidth(vars.breakpoints.tablet)`
        width:calc(100% + 80px);
        margin-left: -40px;
        margin-top: 60px;
        margin-bottom: -60px !important;
        position:relative;
        min-height: 110vw;
        ${Hero} + ${Section} & {
          margin-top: 0;
        }
      `}
      ${maxWidth(vars.breakpoints.mobile)`
        width:calc(100% + 40px);
        margin-left: -20px;
      `}
    `}
`;

Content.Images = styled.div``;

Content.Gallery = styled.div`
  position: relative;
  width: calc(100% + ${vars.container.spaceDesktop}px);
  margin-top: 40px;

  ${maxWidth(vars.breakpoints.sDesktop)`
    width: calc(100% + ${vars.container.spaceXsDesktop}px);
  `}

  ${maxWidth(vars.breakpoints.tablet)`
    width: calc(100% + ${vars.container.spaceTablet}px);
  `}

  ${maxWidth(vars.breakpoints.tablet - 1)`
    width: calc(100% + ${vars.container.spaceTablet}px * 2);
    margin-left: -${vars.container.spaceTablet}px;
  `}

  ${maxWidth(vars.breakpoints.mobile)`
    width: calc(100% + ${vars.container.spaceMobile}px * 2);
    margin-left: -${vars.container.spaceMobile}px;
    margin-top: 32px;
  `}
`;

Content.Credo = styled.div`
  border-top: 1px solid var(--color-decor);
  border-bottom: 1px solid var(--color-decor);
  padding: 40px 0;
  display: flex;
  gap: 40px;
  align-items: center;
  picture,
  video {
    width: 328px;
    max-width: 100%;
    flex-shrink: 0;
  }
  ${maxWidth(vars.breakpoints.xsDesktop)`
      flex-direction:column;
    `}
  ${maxWidth(vars.breakpoints.tablet)`
      flex-direction:row;
    `}
  ${maxWidth(vars.breakpoints.mTablet)`
    flex-direction:column;

    picture, video {
      width:unset;
      }
  `}
`;

Content.Quote = styled.div`
  background: #f8f8f8;
  padding: 50px;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  flex-direction: column;
  gap: 20px;
  position: relative;

  ${maxWidth(vars.breakpoints.tablet)`
    padding: 35px 20px;
    gap:15px;

    h3 {
      font-size: 20px;
      line-height: 150%;
      margin-bottom:15px;
    }
  `}

  &:not(:first-child) {
    margin-top: 50px;
  }

  & > * {
    margin: 0;
  }

  &::after {
    content: '';
    display: block;
    width: 61px;
    height: 42px;
    position: absolute;
    top: -17px;
    left: 48px;
    background-image: url("data:image/svg+xml,%3Csvg width='61' height='42' viewBox='0 0 61 42' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M23.069 37.9629L23.0742 37.958L23.0792 37.9529C25.5811 35.4425 26.8127 32.1066 26.8127 28C26.8127 23.8194 24.976 20.4812 21.3627 18.0184L28.9608 1.20591L29.2798 0.5H28.5052H16.3471H16.085L15.9359 0.715555L4.17642 17.7156L4.17608 17.7161C1.73438 21.2551 0.5 24.9525 0.5 28.8C0.5 32.5232 1.74252 35.5925 4.24369 37.9629L4.24357 37.963L4.25359 37.972C6.87558 40.3261 10.0528 41.5 13.756 41.5C17.4592 41.5 20.5758 40.3258 23.069 37.9629ZM53.5639 37.9629L53.5691 37.958L53.5741 37.9529C56.0759 35.4425 57.3076 32.1066 57.3076 28C57.3076 23.8194 55.4709 20.4812 51.8576 18.0184L59.4556 1.20591L59.7747 0.5H59H46.8419H46.5798L46.4307 0.715555L34.6713 17.7156L34.6709 17.7161C32.2292 21.2551 30.9948 24.9525 30.9948 28.8C30.9948 32.5232 32.2374 35.5925 34.7385 37.9629L34.7384 37.963L34.7484 37.972C37.3704 40.3261 40.5477 41.5 44.2509 41.5C47.954 41.5 51.0706 40.3258 53.5639 37.9629Z' stroke='%23B69B64' /%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;

    ${maxWidth(vars.breakpoints.tablet)`
      width: 57px;
      height: 39px;
      top: -14px;
      left: 20px;
    `}
  }

  p {
    font-size: 18px;
    line-height: 150%;
  }
`;

Content.MissionRow = styled.div`
  display: flex;
  align-items: stretch;
  background: #f8f8f8;
  picture {
    width: 420px;
    max-width: 100%;
    flex-shrink: 0;
    align-self: center;
  }
  ${Content.Html} {
    padding: 50px;
    font-size: 18px;
    line-height: 1.5;
    color: #000;
  }
  ${maxWidth(vars.breakpoints.xsDesktop)`
      flex-direction:column;
    `}
  ${maxWidth(vars.breakpoints.tablet)`
    flex-direction:row;

    ${Content.Html} {
      padding: 30px 20px;
    }
  `}

  ${maxWidth(900)`
    flex-direction:column;

    picture,img {
      width:100%;
    }
  `}
`;

Content.Empty = styled.div`
  padding: 32px 0 48px;
`;

Content.Loadable = styled.div`
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    width: 100%;
    height: 100%;
    opacity: 0;
    pointer-events: none;
    background: rgba(255, 255, 255, 0.5);
    transition: opacity 0.3s ease-out;
  }

  ${(props) =>
    props.isLoading &&
    css`
      &::after {
        opacity: 1;
        pointer-events: all;
      }
    `}
`;

Content.Description = styled.div`
  &:not(:last-child) {
    margin-bottom: 32px;
  }
  ${(props) =>
    props.type &&
    props.type == 'qualification' &&
    css`
      color: #323232;
      h3 {
        font-weight: 700;
        color: #323232;
        margin-bottom: 25px;
      }
      h4 {
        margin-bottom: 5px;
        margin-top: 20px;
        color: var(--color-decor);
      }
    `}
`;

Content.Reviews = styled.div`
  --review-font-size: 16px;

  .swiper-container {
    overflow: visible;
  }

  &:not(:last-child) {
    margin-bottom: 32px;
  }

  ${minWidth(vars.breakpoints.tablet)`

    .swiper-wrapper {
      display: block;
    }

  `}

  ${maxWidth(vars.breakpoints.tablet - 1)`
    .swiper-container {
      display: flex;
    }

    .swiper-wrapper {
      height: auto;
    }
  `}
`;

Content.Link = styled.a`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 20px;
  line-height: 1.2;
  text-transform: uppercase;
  color: #323232;
  &:hover {
    &:after {
      transform: translateX(5px);
    }
  }

  ${(props) =>
    props.arrow &&
    css`
      justify-content: flex-end;
      &:after {
        content: '';
        display: block;
        width: 23px;
        height: 16px;
        flex-shrink: 0;
        background-image: url("data:image/svg+xml,%3Csvg width='23' height='16' viewBox='0 0 18 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.56189 11.5619L11.1238 6.00004L5.56189 0.438152' stroke='%23B69B64' /%3E%3Cpath d='M11.2628 11.5619L16.8247 5.99997L11.2628 0.43809' stroke='%23B69B64' /%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-size: 100% 100%;
        background-position: center;
        transition: transform 0.3s ease-out;
      }
    `}

  ${ArrowButton} {
    margin-left: 16px;
    margin-top: -16px;
  }

  &:active {
    --arrow-btn-bg: var(--color-decor-hover);
  }

  ${noTouch()`
    &:hover {
      --arrow-btn-shadow-opacity: 1;
    }
  `}

  ${maxWidth(vars.breakpoints.mTablet)`
    ${(props) =>
      props.arrow &&
      css`
        justify-content: center;
      `}

    ${ArrowButton} {
      display: none;
    }
  `}
`;

Content.BackLink = styled.div`
  display: none;

  ${maxWidth(vars.breakpoints.tablet)`
    display:flex;
    align-items:center;
    gap:20px;
    padding:14px 0;
    font-size: 20px;
    line-height: 150%;
    color: #323232;
    border-bottom: 1px solid #E2E2E2;
    margin-bottom:30px;

    &::before {
      content:'';
      display;block;
      width:18px;
      height:16px;
      background-image: url("data:image/svg+xml,%3Csvg width='18' height='16' viewBox='0 0 18 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cline x1='17.1484' y1='8.12988' x2='1.14844' y2='8.12988' stroke='%23B69B64'/%3E%3Cpath d='M7.88772 14.8896L0.998098 8L7.88772 1.11038' stroke='%23B69B64'/%3E%3C/svg%3E%0A");
      background-repeat:no-repeat;
      background-position:center;
      background-size:contain;
    }
  `}
`;

Content.Features = styled.ul`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-gap: 60px 30px;

  ${maxWidth(vars.breakpoints.xsDesktop)`
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    grid-gap: 40px 30px;
  `}

  ${maxWidth(vars.breakpoints.tablet)`
    grid-template-columns: repeat(3, minmax(0, 1fr));
    grid-gap: 40px;
  `}

  ${maxWidth(900)`
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  `}

  ${maxWidth(vars.breakpoints.mTablet)`
    grid-template-columns: minmax(0, 1fr);
  `}
`;

Content.LoadBtn = styled.div``;

Content.Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 60px;
  ${Content.Link} {
    margin-left: auto;
  }
`;

Content.SpecialistInfo = styled.p`
  margin-top: -22px;
  margin-bottom: 30px;
  font-size: 18px;
  line-height: 1.2;
  color: #acacac;
  white-space: pre-wrap;

  ${maxWidth(vars.breakpoints.tablet)`
    margin-top: -12px;
  `}
`;
Content.SpecialistSubtitle = styled.div`
  font-weight: 500;
  font-size: 18px;
  color: #323232;
  padding: 15px 0 10px;
`;

Content.SpecialistServices = styled.div`
  display: flex;
  gap: 7px;
  flex-wrap: wrap;
  padding: 0 0 15px;
  ${maxWidth(vars.breakpoints.tablet)`
    overflow-x: auto;
    margin-left: -40px;
    padding-left: 40px;
    padding-right: 40px;
    width:calc(100% + 80px);
    &::-webkit-scrollbar {
      width:0;
      height:0;
      }
    ${Button} {
      text-wrap:nowrap;
      width:max-content;
    }
  `}
  ${maxWidth(vars.breakpoints.mobile)`
    margin-left:-20px;
    padding-left: 20px;
    padding-right: 20px;
    width:calc(100% + 40px);
  `}
`;

Content.Certificates = styled.div`
  column-count: 2;
  column-fill: balance;
  column-gap: 30px;
  ${maxWidth(vars.breakpoints.mobile)`
    display:flex;
    flex-direction:column;
    gap:30px;
  `}
`;

Content.Certificate = styled.div`
  display: flex;
  flex-direction: column;
  background: #fff;
  padding: 30px;
  margin-bottom: 30px;
  page-break-inside: avoid;
  break-inside: avoid;

  ${maxWidth(vars.breakpoints.mobile)`
    margin-bottom: 0;
  `}
`;

Content.CertificateName = styled.div`
  font-size: 16px;
  line-height: 150%;
  color: #323232;
  margin-top: 20px;
  margin-bottom: 10px;
`;
Content.CertificateText = styled.div`
  & > * {
    font-size: 14px;
    color: #acacac;
  }
`;

Content.SocialList = styled.div`
  --social-width: 40px;
  --social-height: 40px;
  --social-border-color: var(--color-border);
  --vlink-icon-width: 40px;
  --vlink-icon-space: 12px;
  --vlink-icon-bg: #fff;

  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  margin-bottom: -18px;
  color: var(--color-decor);

  & > *:not(:last-child) {
    margin-right: 20px;
    margin-bottom: 18px;

    ${maxWidth(vars.breakpoints.tablet)`
      margin-right: 16px;
      margin-bottom: 14px;
    `}
  }

  ${(props) =>
    props.absolute &&
    css`
      position: absolute;
      top: 20px;
      right: 20px;
    `}

  ${maxWidth(vars.breakpoints.mobile)`
    --vlink-font-size: 12px;
  `}
`;

Content.WikiArticles = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);

  ${maxWidth(vars.breakpoints.lMobile)`
    grid-template-columns: minmax(0, 1fr);
  `}
`;

Content.Articles = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 50px;

  ${maxWidth(vars.breakpoints.xsDesktop)`
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 40px;
  `}

  ${maxWidth(900)`
    grid-template-columns: minmax(0, 1fr);
    gap: 30px;
  `}
`;

Content.Contacts = styled(Section).attrs({ as: 'div' })`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  grid-gap: 40px;

  ${maxWidth(vars.breakpoints.tablet - 1)`
    grid-gap: 32x;
  `}

  ${maxWidth(vars.breakpoints.lMobile)`
    grid-template-columns: minmax(0, 1fr);
  `}
`;

Content.Map = styled(Section).attrs({ as: 'div' })`
  width: 100%;
  height: 500px;
  max-height: 100%;
  overflow: hidden;
  & > * {
    height: 100%;
    & > * {
      width: 100%;
    }
  }
`;

Content.SideInfo = styled.div`
  &:not(:first-child) {
    margin-top: 32px;
  }

  &:not(:last-child) {
    margin-bottom: 32px;
  }

    ${(props) =>
    props.tabletHidden &&
    css`
       ${maxWidth(vars.breakpoints.tablet)`
        display:none;
      `}
    `}
`;

Content.MainInfo = styled(Section).attrs({ as: 'div' })`
 ${(props) =>
    props.tabletVisible &&
    css`
      display:none;
      
      ${maxWidth(vars.breakpoints.tablet)`
        display:block;
      `}
    `}

`;

Content.Search = styled.div`
  margin-bottom: 8px;
`;

Content.Video = styled.div`
  position: relative;
  padding-top: 56.25%;
  width: 100%;
  overflow: hidden;

  &:not(:first-child) {
    margin-top: 40px;

    ${maxWidth(vars.breakpoints.mobile)`
      margin-top: 32px;
    `}
  }

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
  }
`;

Content.MobileLink = styled.div`
  display:none;

  ${maxWidth(vars.breakpoints.tablet)`
    display:block;
      ${Anchors.Link} {
        display: flex;
        margin-top: -60px;
        min-height: 42px;
        color: var(--color-decor);  
        position:relative;
        z-index:2;
        &::after {
          content: "";
          display: block;
          width: 18px;
          height: 12px;
          margin-left: 5px;
          order: 1;
          background-image: url("data:image/svg+xml,%3Csvg width='18' height='12' viewBox='0 0 18 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.56189 11.562L11.1238 6.00013L5.56189 0.438243' stroke='%23B69B64' /%3E%3Cpath d='M11.2628 11.5615L16.8247 5.99964L11.2628 0.437755' stroke='%23B69B64' /%3E%3C/svg%3E");          background-repeat: no-repeat;
          background-size: 100% 100%;
          background-position: center center;
          transition: transform 0.3s ease-out;

        }
      }
  `}
`

export default Content;
